/**
 * Matching a scanned packing slip to the sales order it belongs to.
 *
 * Pure: the store action fetches the candidates (orders by PO, acks by vendor
 * order number) and this decides. Keeping the decision free of Firestore lets
 * it be pinned in a spec and dry-run against real slips.
 *
 * What a real Quaker truck (2026-09-15, seven slips) taught the rules:
 * - The vendor order number is the strongest key. The PO field on a slip is
 *   typed by the office at order time and comes back as "0390086A PARTS",
 *   "03900086A-2", "0180002B-BALAN" or "HELMUTH PART" -- the order number
 *   is printed by the vendor's system and was right seven times out of seven.
 * - Quaker prints its number with a two-digit release ("D50071 01") while the
 *   ack holds release 00, so numbers compare on their base.
 * - One PO can carry orders from several vendors (50100033A: Quaker and
 *   ThermaTru; 01000004B: Quaker and Origin), so a PO match is narrowed by the
 *   vendor's name before it is trusted on its own.
 */

import { uniqBy } from "lodash";
import { poMatches } from "./vendorOrderNumber";

/** Upper-cased, trimmed string for case-insensitive comparison. */
export const normKey = (value) =>
  value == null ? "" : String(value).trim().toUpperCase();

/**
 * A vendor order number without its release suffix: "D50071 01" and
 * "D50071 00" are releases of the same order. Numbers with no such suffix
 * (Marvin "SNA06175", Kolbe "1467188", ThermaTru "O-492124") are unchanged.
 */
export const vendorOrderBase = (value) =>
  normKey(value).replace(/\s+\d{2}$/, "");

// Words that say what kind of company it is, not which one.
const VENDOR_NOISE = new Set([
  "windows",
  "window",
  "doors",
  "door",
  "and",
  "inc",
  "llc",
  "co",
  "company",
  "residential",
  "commercial",
  "the",
  "of",
]);

const vendorWords = (name) =>
  String(name || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word && !VENDOR_NOISE.has(word));

/**
 * Whether the vendor printed on a slip ("Quaker Windows & Doors") names the
 * same company as an order's vendor ("Quaker Residential"): they share a
 * distinguishing word. Blank on either side is no evidence either way.
 */
export const vendorMatches = (slipVendor, orderVendor) => {
  const a = vendorWords(slipVendor);
  const b = new Set(vendorWords(orderVendor));
  return a.length > 0 && b.size > 0 && a.some((word) => b.has(word));
};

/**
 * The POs a slip's PO field names. One vendor order can carry several jobs'
 * material, and its slip then lists every job's PO in the one field: a Kolbe
 * slip read 2026-10-06 as "01000048A, 04000026A, 01800047A, S01800038A-FSO".
 * Only a list separator splits it -- a comma, semicolon or line break -- not a
 * space or a dash, which a single PO can carry ("04300086A CUSTOM-PO",
 * "S05400025B-FSO").
 */
export const slipPOs = (poNumber) =>
  String(poNumber == null ? "" : poNumber)
    .split(/[,;\n]/)
    .map((po) => po.trim())
    .filter(Boolean);

/** A trimmed order summary the reconciliation UI can render. */
export const toOrderCandidate = (order) => ({
  orderID: order.id,
  projectID: order.projectID,
  saleID: order.saleID,
  orderPO: order.data.orderPO || (order.projectID || "") + (order.saleID || ""),
  vendor: order.data.vendor || "",
});

/**
 * Decide which order a slip belongs to.
 *
 * A near miss on the vendor order number -- an ack typed with a zero too
 * many, or one character off (see util/vendorOrderNumber.js) -- is never a
 * match: it is offered as a suggestion, first among the candidates, with the
 * reason, and a person confirms it. The PO is compared on the sale it names,
 * so Marvin's copy of "06400017A" keyed without its leading zero still
 * counts as exact.
 *
 * @param {Object} slip - { poNumber, vendorOrderNumber, vendorName, ... }
 * @param {Object} found
 * @param {Array<{order, ack}>} found.byVendorOrder - orders whose ack carries
 *   the slip's vendor order number (base match), with that ack
 * @param {Array} found.byPO - orders from the broad PO search
 * @param {Array<{order, ack, reason}>} found.nearVendorOrder - orders whose
 *   ack carries a near miss of the slip's number, with why it is close
 * @returns {{ match, matchedBy, candidates, suggestions }} matchedBy is one of
 *   "po+von", "von", "po+vendor", "po", or null when a person must decide;
 *   suggestions are `{ orderID, ack, reason }`, best first
 */
export const decideSlipMatch = (
  slip,
  { byVendorOrder = [], byPO = [], nearVendorOrder = [] }
) => {
  const result = {
    slip,
    match: null,
    matchedBy: null,
    candidates: [],
    suggestions: [],
  };

  // An ack still open beats one received long ago under a re-used number.
  const open = byVendorOrder.filter(({ ack }) => !ack || !ack.fullyReceived);
  const vonOrders = uniqBy(
    (open.length ? open : byVendorOrder).map(({ order }) => order),
    "id"
  );

  // The PO search is a prefix search; only an exact PO (or the composed
  // project + sale) counts as a match. Exact means the same sale, so a PO
  // that lost its leading zero on the vendor's side still qualifies. A slip
  // listing several POs matches an order on any of them; several exact
  // orders then go to the vendor's name, or to a person.
  const pos = slipPOs(slip.poNumber);
  const exact = byPO.filter((order) =>
    pos.some(
      (po) =>
        poMatches(po, order.data.orderPO) ||
        poMatches(po, (order.projectID || "") + (order.saleID || ""))
    )
  );

  // Near misses lead the list only when nothing matched outright; a person
  // still has to choose them.
  const near = vonOrders.length
    ? []
    : uniqBy(
        nearVendorOrder.filter(({ order }) => order),
        ({ order }) => order.id
      );
  result.suggestions = near.map(({ order, ack, reason }) => ({
    orderID: order.id,
    ack: ack
      ? {
          id: ack.id,
          projectID: ack.projectID || order.projectID,
          saleID: ack.saleID || order.saleID,
          orderID: ack.orderID || order.id,
          vendorOrderNumber: ack.vendorOrderNumber || "",
        }
      : null,
    reason: reason || null,
  }));

  const candidates = uniqBy(
    [
      ...vonOrders,
      ...(exact.length ? exact : []),
      ...near.map(({ order }) => order),
      ...(exact.length ? [] : byPO),
    ],
    "id"
  );
  result.candidates = candidates.map(toOrderCandidate);
  if (!candidates.length) return result;

  const decide = (order, matchedBy) => {
    result.match = toOrderCandidate(order);
    result.matchedBy = matchedBy;
    return result;
  };
  const isExactPO = (order) => exact.some((o) => o.id === order.id);

  if (vonOrders.length === 1) {
    return decide(vonOrders[0], isExactPO(vonOrders[0]) ? "po+von" : "von");
  }
  if (vonOrders.length > 1) {
    // Several orders carry the same vendor order number (a duplicated order):
    // the PO decides if it singles one out, otherwise a person does.
    const both = vonOrders.filter(isExactPO);
    return both.length === 1 ? decide(both[0], "po+von") : result;
  }

  // No vendor-order hit: the PO alone, narrowed by the vendor's name.
  if (!exact.length) return result;
  if (exact.length === 1) return decide(exact[0], "po");
  const byVendor = exact.filter((order) =>
    vendorMatches(slip.vendorName, order.data.vendor)
  );
  return byVendor.length === 1 ? decide(byVendor[0], "po+vendor") : result;
};
